import { useEffect, useRef } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth-store';
import { api } from '@/lib/api';
import { Header } from '@/components/layout/Header';
import { Sidebar } from '@/components/layout/Sidebar';
import { Footer } from '@/components/layout/Footer';
import { PwaInstallBanner } from '@/components/PwaInstallBanner';
import { GarageOnboarding } from '@/components/GarageOnboarding';
import { detectContentCategory, trackContentVisit } from '@/components/widgets/ContinueLearningWidget';
import { recordSessionPage } from '@/components/widgets/SessionProgressBar';
import { upsertMeta, buildBreadcrumbSchema, buildArticleSchema, injectJsonLd } from '@/lib/utils';
import { useScrollDepth } from '@/hooks/useScrollDepth';
import { trackReadComplete, trackReadingTime, detectAndTrackShareArrival } from '@/lib/analytics';
import { ReadProgressBar } from '@/components/ui/ReadProgressBar';
import { ProStickyBar } from '@/components/ProStickyBar';
import { PRO_ENABLED } from '@/lib/features';
import { ShareArrivalBanner } from '@/components/ShareArrivalBanner';
import { ScrollToTopFab } from '@/components/ui/ScrollToTopFab';

/**
 * T-76: the public read surface.
 *
 * This layout used to redirect EVERY logged-out visitor to /welcome, which
 * walled all ~200 routes behind a signup form. For SEO editorial the public
 * read surface is the business, so the default is now PUBLIC and only the
 * paths below require a session.
 *
 * Deliberately a deny-list, not an allow-list. An allow-list of readable
 * routes means every new article, vehicle or author page ships invisible to
 * logged-out readers and to crawlers until someone remembers to register it —
 * the same silent-omission failure that caused this bug. Default-public means
 * a new private feature has to opt IN, which is a visible, deliberate act.
 *
 * Login is required only for personalization, garages, comments, saves and
 * alerts (the operator's list). Everything else reads logged out.
 */
const PRIVATE_PREFIXES = [
  '/bookmarks',
  '/settings',
  '/history',
  '/notifications',
  '/garages',
  '/garage',
  '/invite',
  '/pro',
  '/leaderboard',
  '/extension',
];

export function isPrivatePath(pathname: string): boolean {
  // Normalise a trailing slash so '/settings/' matches '/settings'.
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  return PRIVATE_PREFIXES.some((p) => path === p || path.startsWith(p + '/'));
}

const BREADCRUMB_JSONLD_ID = 'seo-106-breadcrumb-jsonld';
const ARTICLE_JSONLD_ID = 'seo-299-article-jsonld';

/** Content route prefixes that should get Article JSON-LD when pages don't set their own. */
const CONTENT_PREFIXES = ['/bikes', '/cars', '/guides', '/data', '/ev', '/scooters', '/launches'];

/** Route prefixes where the read progress bar + scroll tracking should appear. */
const READABLE_PREFIXES = [...CONTENT_PREFIXES, '/post'];

// ANALYTICS-047 + ANALYTICS-019: Track scroll depth, read completion, and
// time-on-article for all content pages (guides, bikes, cars, etc.).
function ContentScrollTracker({ pathname }: { pathname: string }) {
  const pageType = pathname.split('/')[1] || 'content';
  useScrollDepth(pageType, pathname);

  const firedRef = useRef(false);
  const startTime = useRef(Date.now());

  useEffect(() => {
    firedRef.current = false;
    startTime.current = Date.now();

    function onScroll() {
      if (firedRef.current) return;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (docHeight <= 0) return;
      if ((window.scrollY / docHeight) * 100 >= 95) {
        firedRef.current = true;
        trackReadComplete(pathname, Date.now() - startTime.current);
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      // ANALYTICS-019: fire time-on-article when leaving any content page
      trackReadingTime(pathname, Date.now() - startTime.current);
    };
  }, [pathname]);

  return null;
}

export default function RootLayout() {
  const { isAuthenticated, accessToken, setUser, setLoading, logout, isLoading } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const checkAuth = async () => {
      if (accessToken) {
        try {
          const user = await api.auth.me();
          setUser(user);
        } catch {
          logout();
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, [accessToken, setUser, setLoading, logout]);

  useEffect(() => {
    if (!isAuthenticated && !isLoading && isPrivatePath(location.pathname)) {
      navigate('/welcome', { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate, location.pathname]);

  // ANALYTICS-016: Detect inbound share attribution (utm_source + utm_medium=share)
  // Runs once on initial page load — sessionStorage deduplicates within a tab session.
  useEffect(() => {
    detectAndTrackShareArrival();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // RET-2: Track content page visits for Continue Learning CTA
  useEffect(() => {
    const category = detectContentCategory(location.pathname);
    if (!category) return;
    // Delay slightly so the page's own useEffect can set document.title first
    const timer = setTimeout(() => {
      const title = document.title || location.pathname;
      trackContentVisit(location.pathname, title, category);
      recordSessionPage(location.pathname);
    }, 300);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  // UI-016: Global canonical link — strip query params, cover all pages
  // UG-014: Self-referencing hreflang tags for future Hindi layer
  useEffect(() => {
    const canonicalUrl = `${window.location.origin}${location.pathname}`;

    // Canonical link
    let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', canonicalUrl);

    function upsertHreflang(hreflang: string, href: string) {
      const selector = `link[rel="alternate"][hreflang="${hreflang}"]`;
      let el = document.querySelector(selector) as HTMLLinkElement | null;
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', 'alternate');
        el.setAttribute('hreflang', hreflang);
        document.head.appendChild(el);
      }
      el.setAttribute('href', href);
    }

    upsertHreflang('en', canonicalUrl);
    upsertHreflang('x-default', canonicalUrl);

    return () => {
      document.querySelector('link[rel="canonical"]')?.remove();
      document.querySelector('link[rel="alternate"][hreflang="en"]')?.remove();
      document.querySelector('link[rel="alternate"][hreflang="x-default"]')?.remove();
    };
  }, [location.pathname]);

  // SEO-106: Inject BreadcrumbList JSON-LD for every non-root page so Google can render
  // rich breadcrumbs in SERPs. Auto-derived from URL structure — no per-page wiring.
  useEffect(() => {
    const schema = buildBreadcrumbSchema(location.pathname, window.location.origin);
    if (schema) injectJsonLd(BREADCRUMB_JSONLD_ID, schema);
    return () => {
      document.getElementById(BREADCRUMB_JSONLD_ID)?.remove();
    };
  }, [location.pathname]);

  // SEO-103: Auto-generate OG + Twitter Card meta tags for pages that don't set their own
  useEffect(() => {
    const timer = setTimeout(() => {
      // If the page already set OG tags via setOgMeta(), don't overwrite
      const existingOgTitle = document.querySelector('meta[property="og:title"]');
      if (existingOgTitle?.getAttribute('content')) return;

      const title = document.title || 'MotoWalla - Your Automotive Homepage';
      const desc =
        document.querySelector('meta[name="description"]')?.getAttribute('content') || '';
      const url = `${window.location.origin}${location.pathname}`;

      upsertMeta('property', 'og:type', 'website');
      upsertMeta('property', 'og:title', title);
      upsertMeta('property', 'og:description', desc);
      upsertMeta('property', 'og:url', url);
      upsertMeta('property', 'og:site_name', 'MotoWalla');
      upsertMeta('name', 'twitter:card', 'summary');
      upsertMeta('name', 'twitter:title', title);
      upsertMeta('name', 'twitter:description', desc);
    }, 350); // After child useEffects set title/description

    return () => {
      clearTimeout(timer);
      // Clean up OG tags on route change so the next page starts fresh
      ['og:type', 'og:title', 'og:description', 'og:url', 'og:site_name'].forEach((key) =>
        document.querySelector(`meta[property="${key}"]`)?.remove(),
      );
      ['twitter:card', 'twitter:title', 'twitter:description'].forEach((key) =>
        document.querySelector(`meta[name="${key}"]`)?.remove(),
      );
    };
  }, [location.pathname]);

  // GROW-003: og:image default fallback. Re-asserts /og-default.png on every route
  // change for any page that didn't set its own image, and recovers if a previous
  // page's cleanup stripped the tag entirely. Twitter:image mirrors og:image.
  useEffect(() => {
    const timer = setTimeout(() => {
      const ogImage = document.querySelector('meta[property="og:image"]') as HTMLMetaElement | null;
      if (!ogImage || !ogImage.getAttribute('content')) {
        upsertMeta('property', 'og:image', '/og-default.png');
      }
      const twImage = document.querySelector('meta[name="twitter:image"]') as HTMLMetaElement | null;
      if (!twImage || !twImage.getAttribute('content')) {
        upsertMeta('name', 'twitter:image', '/og-default.png');
      }
    }, 450); // After SEO-103 fallback (350ms) and any per-page setOgMeta
    return () => clearTimeout(timer);
  }, [location.pathname]);

  // SEO-299: Global Article JSON-LD fallback for content pages.
  // If the page's own useEffect already injected Article/NewsArticle JSON-LD, this is a no-op.
  // Fires after child effects (400 ms) so per-page schemas take priority.
  useEffect(() => {
    const isContent = CONTENT_PREFIXES.some((p) => location.pathname.startsWith(p));
    if (!isContent) return;

    const timer = setTimeout(() => {
      // Check if any existing script already covers Article/NewsArticle
      const scripts = document.querySelectorAll('script[type="application/ld+json"]');
      for (const s of scripts) {
        if (s.id === ARTICLE_JSONLD_ID) continue;
        try {
          const data = JSON.parse(s.textContent || '');
          const items = Array.isArray(data) ? data : [data];
          if (items.some((d: Record<string, unknown>) => d['@type'] === 'Article' || d['@type'] === 'NewsArticle')) return;
        } catch { /* ignore malformed */ }
      }

      const title = document.title || 'MotoWalla';
      const desc =
        document.querySelector('meta[name="description"]')?.getAttribute('content') || '';
      if (!title || title === 'MotoWalla') return; // Skip generic pages

      injectJsonLd(
        ARTICLE_JSONLD_ID,
        buildArticleSchema({
          headline: title,
          description: desc,
          datePublished: new Date().toISOString().slice(0, 10),
        }),
      );
    }, 400);

    return () => {
      clearTimeout(timer);
      document.getElementById(ARTICLE_JSONLD_ID)?.remove();
    };
  }, [location.pathname]);

  // Show loading while checking auth
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg-primary">
        <div className="flex flex-col items-center gap-4">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary-500 border-t-transparent" />
          <span className="text-sm text-text-secondary">Loading...</span>
        </div>
      </div>
    );
  }

  // T-76: do NOT blank the page for anonymous visitors. This early `return null`
  // was the second half of the wall: even after the redirect was narrowed, every
  // public route still rendered an empty document to logged-out humans and to
  // crawlers, which is worse than a redirect because it returns 200 with no content.
  // Private paths are handled by the redirect effect above; everything else renders.
  if (!isAuthenticated && isPrivatePath(location.pathname)) {
    return null;
  }

  const isReadable = READABLE_PREFIXES.some((p) => location.pathname.startsWith(p));
  const isContentPage = CONTENT_PREFIXES.some((p) => location.pathname.startsWith(p));

  return (
    <div className="flex min-h-screen flex-col bg-bg-primary">
      <Header />
      {isReadable && <ReadProgressBar />}
      {isContentPage && <ContentScrollTracker pathname={location.pathname} />}
      <div className="flex flex-1">
        <Sidebar />
        <main className="flex-1 overflow-auto">
          <Outlet />
          {/* T-196: inside main, after the outlet — main is the scroll container, so a
              footer outside it would pin to the viewport instead of ending the document. */}
          <Footer />
        </main>
      </div>
      <PwaInstallBanner />
      <GarageOnboarding />
      {/* T-120: Pro is uncommissioned and checkout 500s (decision 105). */}
      {PRO_ENABLED && <ProStickyBar />}
      <ShareArrivalBanner />
      <ScrollToTopFab />
    </div>
  );
}
